import { useMemo } from "react";
import {
  Button,
  Card,
  Recommendation,
  RecommendationDialogItems,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Dialog, ScrollView, Text, View } from "tamagui";

import { chunkArray } from "~/utils/chunk-array";
import { useAutoScroll } from "~/hooks";
import type { RecommendationData } from "./utils";

const defaultImage = "/recommendations/default.png";

export const RecommendationsGrid: React.FC<{
  data?: RecommendationData[];
}> = ({ data }) => {
  const { t } = useTranslation("metrics");
  const { trackHeightValue, scrollRef, resetOnClose } = useAutoScroll();
  const formattedData = useMemo(() => chunkArray(data ?? [], 3), [data]);

  if (!data) return null;

  return (
    <View flex={1} alignSelf="stretch" gap={"$1.5"} width={"100%"}>
      {formattedData.map((item, index) => (
        <View
          justifyContent="center"
          flex={3}
          flexDirection="row"
          key={index}
          gap={"$1.5"}
        >
          {item.map(({ text, tag, tagColor, dialog, id, image }, i) => (
            <Recommendation.Container
              maxWidth={306}
              data-id={`recommendation-container-${i}`}
              width={"calc(33% - 8px)"}
              justifyContent="space-between"
              key={id}
            >
              <View alignItems="center" gap={"$4"}>
                <Recommendation.Image path={image ?? defaultImage} />
                <View gap={"$1"}>
                  {tag && (
                    <Recommendation.Tag
                      borderRadius={999}
                      data-id={`recommendation-tag-${id}`}
                      backgroundColor={tagColor ?? "$pinkmain-500"}
                      color={"white"}
                    >
                      {tag}
                    </Recommendation.Tag>
                  )}
                  <Recommendation.Text data-id={`recommendation-text-${id}`}>
                    {text}
                  </Recommendation.Text>
                </View>
              </View>

              <Dialog>
                <Dialog.Trigger asChild>
                  <Recommendation.Button
                    data-id={`recommendation-button`}
                    maxHeight={"$6"}
                  >
                    <Text
                      color="$gray-primary-900"
                      fontFamily="$subtitle"
                      fontSize="$3"
                      fontWeight="$5"
                      textAlign="center"
                    >
                      Переглянути
                    </Text>
                  </Recommendation.Button>
                </Dialog.Trigger>

                <Dialog.Portal>
                  <Dialog.Overlay
                    zIndex={0}
                    key={"recommendations-dialog-overlay"}
                    style={{
                      backgroundColor: "#6A696966",
                      backdropFilter: "blur(10px)",
                    }}
                  />
                  <RecommendationDialogItems.Container
                    data-id={`recommendation-dialog-container`}
                  >
                    <RecommendationDialogItems.Image
                      path={image ?? defaultImage}
                    />

                    <Card
                      gap={"$5"}
                      variant="borderless"
                      backgroundColor={"white"}
                      padding={"$4"}
                      data-id={`recommendation-dialog-content-${id}`}
                    >
                      <ScrollView
                        scrollEventThrottle={16}
                        ref={scrollRef}
                        onContentSizeChange={(_, height) =>
                          trackHeightValue(height)
                        }
                        paddingBottom={"$2"}
                        maxHeight={400}
                      >
                        <View gap={"$1"}>
                          {dialog.tag && (
                            <RecommendationDialogItems.Tag
                              data-id={`recommendation-dialog-tag-${id}`}
                              color="white"
                              rounded="md"
                              backgroundColor={
                                dialog.tagColor ?? "$pinkmain-500"
                              }
                              alignSelf="flex-start"
                            >
                              {dialog.tag}
                            </RecommendationDialogItems.Tag>
                          )}
                          <RecommendationDialogItems.Title
                            data-id={`recommendation-dialog-title-${id}`}
                          >
                            {dialog.title}
                          </RecommendationDialogItems.Title>
                        </View>
                        <View>
                          <View
                            data-id={`recommendation-dialog-description-${id}`}
                          >
                            <RecommendationDialogItems.RenderMd
                              md={dialog.markdown}
                            />
                          </View>

                          <RecommendationDialogItems.Accordion
                            data-id={`recommendation-dialog-accordion-${id}`}
                            data={dialog?.accordion ?? []}
                          >
                            {({ content }) => (
                              <RecommendationDialogItems.RenderMd
                                md={content}
                              />
                            )}
                          </RecommendationDialogItems.Accordion>
                        </View>
                      </ScrollView>
                    </Card>

                    <Dialog.Close asChild>
                      <Button
                        data-id={`recommendation-dialog-close-${id}`}
                        variant="primary"
                        rounded="lg"
                        maxWidth={300}
                        width="100%"
                        alignSelf="center"
                        size="lg"
                        onPress={resetOnClose}
                      >
                        {t("recommendations.buttons.close")}
                      </Button>
                    </Dialog.Close>
                  </RecommendationDialogItems.Container>
                </Dialog.Portal>
              </Dialog>
            </Recommendation.Container>
          ))}
        </View>
      ))}
    </View>
  );
};
